import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import FormatEditor from '../components/FormatEditor';
import AdminHeader from '../components/AdminHeader';
import AdminTabs from '../components/AdminTabs';
import Toast from '../components/Toast';
import ConfirmationDialog from '../components/ConfirmationDialog';

import { API_BASE_URL } from '../config/api';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper function to make authenticated requests
const authAxios = {
  get: (url, config = {}) => axios.get(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  put: (url, data, config = {}) => axios.put(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  post: (url, data, config = {}) => axios.post(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  delete: (url, config = {}) => axios.delete(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } })
};

function FormatsAdminPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formats, setFormats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingFormat, setEditingFormat] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  useEffect(() => {
    document.title = 'Format Management - Admin';
    fetchFormats();
  }, []);

  // Handle edit query parameter
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && formats.length > 0) {
      const formatToEdit = formats.find(f => f.id === editId);
      if (formatToEdit) {
        setEditingFormat(formatToEdit);
        setShowCreateForm(false);
      } else {
        // Format not found in current list, fetch it directly
        const fetchFormat = async () => {
          try {
            const response = await authAxios.get(`${API_BASE_URL}/admin/formats/${editId}`);
            if (response.data.success) {
              setEditingFormat(response.data.data);
              setShowCreateForm(false);
            }
          } catch (err) {
            console.error('Error fetching format:', err);
          }
        };
        fetchFormat();
      }
    }
  }, [searchParams, formats]);

  const fetchFormats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/formats`);
      if (response.data.success) {
        setFormats(response.data.data);
      } else {
        setError(response.data.error || 'Failed to load formats');
      }
    } catch (err) {
      console.error('Error fetching formats:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load formats');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (formatId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Format',
      message: 'Are you sure you want to delete this format? This action cannot be undone.',
      confirmText: 'Delete',
      confirmButtonClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          const response = await authAxios.delete(`${API_BASE_URL}/admin/formats/${formatId}`);
          if (response.data.success) {
            setToast({ message: 'Format deleted successfully', type: 'success' });
            await fetchFormats();
          } else {
            setToast({ message: response.data.error || 'Failed to delete format', type: 'error' });
          }
        } catch (err) {
          console.error('Error deleting format:', err);
          setToast({ message: err.response?.data?.error || err.message || 'Failed to delete format', type: 'error' });
        }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleEdit = (format) => {
    setEditingFormat(format);
    setShowCreateForm(false);
  };

  const handleCreate = () => {
    setEditingFormat(null);
    setShowCreateForm(true);
  };

  const handleSaveComplete = () => {
    setEditingFormat(null);
    setShowCreateForm(false);
    fetchFormats();
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading formats...</p>
          </div>
        </div>
      </div>
    );
  }

  if (editingFormat || showCreateForm) {
    return (
      <FormatEditor
        format={editingFormat}
        onSave={handleSaveComplete}
        onCancel={() => {
          setEditingFormat(null);
          setShowCreateForm(false);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <AdminHeader />

        <AdminTabs />

        {/* Quick Links */}
        <div className="mb-6 flex flex-wrap gap-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <a
              href="/admin/add-design"
              className="text-blue-700 hover:text-blue-900 font-medium underline flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Design
            </a>
          </div>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <a
              href="/admin/formats"
              className="text-blue-700 hover:text-blue-900 font-medium underline flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
              </svg>
              Formats
            </a>
          </div>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <button
              onClick={() => navigate('/admin', { state: { activeTab: 'json-editor' } })}
              className="text-blue-700 hover:text-blue-900 font-medium underline flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              Design Editor
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mb-6 flex justify-end">
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            + New Format
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Formats List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mockups
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {formats.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No formats found. Create your first format to get started.
                  </td>
                </tr>
              ) : (
                formats.map((format) => (
                  <tr key={format.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {format.id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {format.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        format.type === 'Digital' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {format.type || 'Physical'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${format.price?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format.mockups?.length || 0} mockup{(format.mockups?.length || 0) !== 1 ? 's' : ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(format)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(format.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Toast Notification */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
        
        {/* Confirmation Dialog */}
        {confirmDialog && (
          <ConfirmationDialog
            isOpen={confirmDialog.isOpen}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmText={confirmDialog.confirmText}
            confirmButtonClass={confirmDialog.confirmButtonClass}
            onConfirm={confirmDialog.onConfirm}
            onCancel={confirmDialog.onCancel}
          />
        )}
      </div>
    </div>
  );
}

export default FormatsAdminPage;

